import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/ui/Logo";
import { useRole } from "@/hooks/useRole";
import { setRole } from "@/lib/auth-mock";
import { cn } from "@/lib/cn";
import { ShieldAlert, LayoutDashboard, Users, Sparkles, Gauge, Package, LineChart, LogOut } from "lucide-react";

const nav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/spark", label: "Spark Studio", icon: Sparkles },
  { to: "/admin/bss", label: "BSS Engine", icon: Gauge },
  { to: "/admin/catalog", label: "Service Catalog", icon: Package },
  { to: "/admin/roi", label: "ROI Intelligence", icon: LineChart },
];

export default function AdminLayout() {
  const role = useRole();
  const navigate = useNavigate();

  if (role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="panel-hi p-10 max-w-lg text-center corner-bracket">
          <ShieldAlert className="text-brand-red mx-auto" size={40} />
          <div className="eyebrow mt-4 !text-brand-red">// Restricted</div>
          <h1 className="display-md text-silver-50 mt-2">INFINI X Admin Console</h1>
          <p className="text-silver-300 mt-3 text-sm">
            This surface is internal-only. Sign in with an INFINI team account to continue.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button className="btn-primary" onClick={() => { setRole("admin"); }}>Grant admin (preview)</button>
            <button className="btn-ghost" onClick={() => navigate("/")}>Back to site</button>
          </div>
          <div className="hairline my-6" />
          <p className="text-xs text-silver-300 font-mono uppercase tracking-widest">
            // preview build uses mock auth · real auth: supabase RLS
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-64 flex-col border-r border-ink-700/60 bg-ink-950/90">
        <div className="p-6"><Logo /></div>
        <div className="px-6 mb-2 flex items-center gap-2">
          <span className="chip-red">Admin</span>
          <span className="eyebrow">// Internal</span>
        </div>
        <nav className="flex-1 px-3 space-y-1 mt-4">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2.5 font-display text-sm uppercase tracking-wider transition",
                  isActive
                    ? "bg-brand-red/15 text-brand-red border-l-2 border-brand-red"
                    : "text-silver-200 hover:bg-ink-800 hover:text-silver-50",
                )
              }
            >
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-ink-700/60">
          <button
            onClick={() => { setRole("guest"); navigate("/"); }}
            className="btn-ghost w-full justify-center"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="h-16 border-b border-ink-700/60 bg-ink-950/70 backdrop-blur flex items-center justify-between px-6">
          <div className="eyebrow">// INFINI X · Admin Console · preview</div>
          <div className="chip-red">Restricted surface</div>
        </div>
        <div className="p-6 md:p-10"><Outlet /></div>
      </main>
    </div>
  );
}
